import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import { PronteerLogo, deliverPdf, sanitizeFilename } from '@/lib/pdf/shared'

export interface ManifestPassengerRow {
  /** Pre-formatted "LASTNAME, Firstname" */
  name: string
  /** Pre-formatted "DD-MMM-YYYY" or '' */
  dateOfBirth?: string
  passportNo?: string
  /** Pre-formatted "DD-MMM-YYYY" or '' */
  passportExpiry?: string
  nationality?: string
}

export interface ExportPassengerManifestParams {
  companyName: string
  aircraftRegistration: string
  date: Date | string
  departureIcao: string
  arrivalIcao: string
  passengers: ManifestPassengerRow[]
  unknownPaxCount: number
  crewCount: number
}

export function formatManifestName(firstName: string, lastName: string): string {
  return [lastName?.toUpperCase(), firstName].filter(Boolean).join(', ')
}

// Sex and Place of Birth have no source in the data model; the columns stay
// blank for handwriting, like the paper form this replaces.
const COLUMNS = [
  { label: 'SURNAME AND NAMES', width: '26%' },
  { label: 'SEX', width: '5%' },
  { label: 'DATE OF BIRTH', width: '11%' },
  { label: 'PLACE OF BIRTH', width: '15%' },
  { label: 'PASSPORT No.', width: '14%' },
  { label: 'EXPIRES', width: '11%' },
  { label: 'NATIONALITY', width: '18%' },
] as const

type RowCells = [string, string, string, string, string, string, string]

const EMPTY_CELLS: RowCells = ['', '', '', '', '', '', '']
const MIN_ROWS = 15

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#0a0a0a',
    flexDirection: 'column',
  },
  headerBand: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 18,
  },
  headerSide: {
    width: 110,
  },
  titleWrap: {
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 10,
    gap: 8,
  },
  fieldLabel: {
    fontSize: 10,
  },
  fieldValue: {
    borderBottomWidth: 1,
    borderBottomColor: '#0a0a0a',
    fontSize: 10,
    textAlign: 'center',
    paddingBottom: 2,
    paddingHorizontal: 8,
  },
  table: {
    marginTop: 8,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: '#0a0a0a',
  },
  tableRow: {
    flexDirection: 'row',
  },
  headCell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#0a0a0a',
    paddingVertical: 4,
    paddingHorizontal: 3,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  bodyCell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#0a0a0a',
    minHeight: 19,
    paddingVertical: 4,
    paddingHorizontal: 4,
    fontSize: 8.5,
    justifyContent: 'center',
  },
  totalsBlock: {
    marginTop: 16,
    gap: 12,
  },
  totalsRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  totalsLabel: {
    fontSize: 10,
    width: 100,
  },
  totalsValue: {
    width: 130,
    borderBottomWidth: 1,
    borderBottomColor: '#0a0a0a',
    fontSize: 10,
    textAlign: 'center',
    paddingBottom: 2,
  },
  signatureBlock: {
    marginTop: 'auto',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 24,
  },
  signatureLabel: {
    fontSize: 11,
  },
  signatureLine: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  signatureDots: {
    fontSize: 9,
    color: '#0a0a0a',
  },
  signatureCaption: {
    fontSize: 8,
    color: '#525252',
    marginTop: 3,
  },
})

function HeaderField({
  label,
  value,
  valueWidth = 110,
}: {
  label: string
  value: string
  valueWidth?: number
}) {
  return (
    <>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={{ ...styles.fieldValue, width: valueWidth }}>{value || ' '}</Text>
    </>
  )
}

export function PassengerManifestDocument(params: ExportPassengerManifestParams) {
  const dateLabel = dayjs(params.date).format('DD-MMM-YYYY')

  const filled: RowCells[] = [
    ...params.passengers.map(
      (p): RowCells => [
        p.name,
        '',
        p.dateOfBirth ?? '',
        '',
        p.passportNo ?? '',
        p.passportExpiry ?? '',
        p.nationality ?? '',
      ],
    ),
    ...Array.from({ length: params.unknownPaxCount }, () => EMPTY_CELLS),
  ]
  const rows: RowCells[] =
    filled.length >= MIN_ROWS
      ? filled
      : [...filled, ...Array.from({ length: MIN_ROWS - filled.length }, () => EMPTY_CELLS)]

  const personsOnBoard =
    params.crewCount + params.passengers.length + params.unknownPaxCount

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.headerBand}>
          <View style={styles.headerSide}>
            <PronteerLogo width={90} />
          </View>
          <View style={styles.titleWrap}>
            <Text style={styles.title}>Passenger Manifest</Text>
          </View>
          <View style={styles.headerSide} />
        </View>

        <View style={styles.fieldRow}>
          <HeaderField label="Owner or Operator" value={params.companyName} valueWidth={180} />
        </View>
        <View style={styles.fieldRow}>
          <HeaderField
            label="Marks of Nationality and Registration"
            value={params.aircraftRegistration}
          />
          <HeaderField label="Flight No" value={params.aircraftRegistration} />
          <HeaderField label="Date" value={dateLabel} />
        </View>
        <View style={styles.fieldRow}>
          <HeaderField label="Departure from" value={params.departureIcao} />
          <HeaderField label="Arrival at" value={params.arrivalIcao} />
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            {COLUMNS.map((col) => (
              <Text key={col.label} style={{ ...styles.headCell, width: col.width }}>
                {col.label}
              </Text>
            ))}
          </View>
          {rows.map((cells, rowIndex) => (
            <View key={rowIndex} style={styles.tableRow} wrap={false}>
              {cells.map((cell, cellIndex) => (
                <View
                  key={cellIndex}
                  style={{ ...styles.bodyCell, width: COLUMNS[cellIndex].width }}
                >
                  <Text>{cell || ' '}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        <View style={styles.totalsBlock}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Number of Crew</Text>
            <Text style={styles.totalsValue}>
              {params.crewCount > 0 ? params.crewCount : ' '}
            </Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Persons on Board</Text>
            <Text style={styles.totalsValue}>{personsOnBoard > 0 ? personsOnBoard : ' '}</Text>
          </View>
        </View>

        <View style={styles.signatureBlock}>
          <Text style={styles.signatureLabel}>Signature</Text>
          <View style={styles.signatureLine}>
            <Text style={styles.signatureDots}>
              ......................................................................
            </Text>
            <Text style={styles.signatureCaption}>Authorized Agent or pilot-in Command</Text>
          </View>
        </View>
      </Page>
    </Document>
  )
}

/**
 * Generate a Passenger Manifest PDF for a flight and hand it off: native
 * share sheet on iOS/Android, file download on web.
 */
export async function exportPassengerManifestPdf(
  params: ExportPassengerManifestParams,
): Promise<void> {
  const blob = await pdf(<PassengerManifestDocument {...params} />).toBlob()

  const filename = `${sanitizeFilename(
    `passenger-manifest-${params.departureIcao}-${params.arrivalIcao}-${dayjs(params.date).format('YYYY-MM-DD')}`,
    'passenger-manifest',
  )}.pdf`

  await deliverPdf(blob, filename, 'Passenger Manifest')
}
